import { ApiRequestError, GatewayUnavailableError, VersionConflictError } from './errors';
import type { ApiResult, Envelope, OperationKind, RequestEnvelope } from './types';
import { emitCommandDone } from './events';

export type ApiChannel = 'app' | 'portal';

export interface ApiClientConfig {
  /** URL query-шлюза (view.*) */
  queryUrl: string;
  /** URL command-шлюза (команды) */
  commandUrl: string;
  /**
   * Канал контура. Определяется ТИПОМ шлюза, а не выбором вызывающего кода:
   * app-клиент ходит в /api/*, портальный — в /api/portal/*. Сервер всё равно
   * подставляет канал сам (вебхук жёстко проставляет channel), но конверт
   * держим честным: подрядчик никогда не отправляет channel='app'.
   */
  channel?: ApiChannel;
  /** Bearer-токен сессии; null — анонимный вызов (напр. auth.login) */
  getToken: () => string | null;
  /**
   * Вызывается на код `unauthenticated`. ЕДИНСТВЕННЫЙ триггер разлогина
   * (conventions.md: правило legacy DEAD_ERRORS не наследуем).
   */
  onUnauthenticated?: () => void;
  fetchFn?: typeof fetch;
  /**
   * Паузы (мс) перед повторами ЧТЕНИЯ, когда шлюз не ответил конвертом: обрыв сети либо ответ прокси
   * (404/429/502 без конверта — так бывало сразу после выкладки). Чтение повторять безопасно; команды
   * не повторяем — у них свой путь с idempotencyKey. По умолчанию два повтора: 400 и 1200 мс.
   */
  queryRetryDelays?: number[];
}

const QUERY_RETRY_DELAYS = [400, 1200];

export interface CommandOptions {
  expectedVersion?: number;
  /** По умолчанию генерируется crypto.randomUUID(); передаётся явно для retry-безопасных черновиков */
  idempotencyKey?: string;
}

export class ApiClient {
  private readonly fetchFn: typeof fetch;

  constructor(private readonly config: ApiClientConfig) {
    this.fetchFn = config.fetchFn ?? ((...args) => fetch(...args));
  }

  /** view.<snake_name> через query-шлюз */
  query<T>(operation: string, payload: Record<string, unknown> = {}): Promise<ApiResult<T>> {
    return this.request<T>('view', operation, payload);
  }

  /** entity.verb через command-шлюз */
  command<T>(
    operation: string,
    payload: Record<string, unknown> = {},
    options: CommandOptions = {},
  ): Promise<ApiResult<T>> {
    // C-15: повтор той же команды с тем же payload после сетевой ошибки/недоступного шлюза
    // переиспользует idempotencyKey — сервер распознаёт replay, дубля не будет.
    const sig = `${operation}|${JSON.stringify(payload)}`;
    const reused = this.retryKeys.get(sig);
    const key = options.idempotencyKey ?? reused ?? crypto.randomUUID();
    return this.request<T>('command', operation, payload, { ...options, idempotencyKey: key }).then(
      (res) => {
        this.retryKeys.delete(sig);
        emitCommandDone(operation);
        return res;
      },
      (err: unknown) => {
        if (err instanceof GatewayUnavailableError) this.retryKeys.set(sig, key);
        else this.retryKeys.delete(sig);
        throw err;
      },
    );
  }

  /** Ключи идемпотентности незавершённых (retryable) попыток: operation|payload → key. */
  private readonly retryKeys = new Map<string, string>();

  private async request<T>(
    kind: OperationKind,
    operation: string,
    payload: Record<string, unknown>,
    options: CommandOptions = {},
  ): Promise<ApiResult<T>> {
    // Короткий сбой прокси или сети при чтении не должен оставлять на экране ошибку: повторяем молча.
    const delays = kind === 'view' ? (this.config.queryRetryDelays ?? QUERY_RETRY_DELAYS) : [];
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await this.requestOnce<T>(kind, operation, payload, options);
      } catch (e) {
        if (!(e instanceof GatewayUnavailableError) || attempt >= delays.length) throw e;
        await new Promise((r) => setTimeout(r, delays[attempt]));
      }
    }
  }

  private async requestOnce<T>(
    kind: OperationKind,
    operation: string,
    payload: Record<string, unknown>,
    options: CommandOptions = {},
  ): Promise<ApiResult<T>> {
    const url = kind === 'command' ? this.config.commandUrl : this.config.queryUrl;

    const envelope: RequestEnvelope = {
      kind,
      operation,
      payload,
      correlationId: crypto.randomUUID(),
      channel: this.config.channel ?? 'app',
    };
    if (kind === 'command') {
      envelope.idempotencyKey = options.idempotencyKey ?? crypto.randomUUID();
    }
    if (options.expectedVersion !== undefined) {
      envelope.expectedVersion = options.expectedVersion;
    }

    const token = this.config.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await this.fetchFn(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(envelope),
      });
    } catch (cause) {
      throw new GatewayUnavailableError('Шлюз недоступен: сетевая ошибка', cause);
    }

    let body: Envelope<T>;
    try {
      body = (await response.json()) as Envelope<T>;
    } catch (cause) {
      throw new GatewayUnavailableError(
        `Шлюз вернул некорректный ответ (HTTP ${response.status})`,
        cause,
      );
    }

    if (body && body.ok === true) {
      return { data: body.data, meta: body.meta };
    }

    if (body && body.ok === false && body.error) {
      const { error, meta } = body;
      // Разлогин по потере сессии: v3 отдаёт no_session / session_expired для
      // отсутствующего или истёкшего токена; unauthenticated — неверный пароль
      // (сессии и так нет). Бизнес-отказы (bad_transition, conflict) сессию не трогают.
      if (error.code === 'no_session' || error.code === 'session_expired' || (error.code === 'unauthenticated' && this.config.getToken())) {
        this.config.onUnauthenticated?.();
      }
      if (error.code === 'version_conflict') {
        throw new VersionConflictError(error, meta);
      }
      throw new ApiRequestError(error, meta);
    }

    throw new GatewayUnavailableError(
      `Шлюз вернул ответ вне контракта конверта (HTTP ${response.status})`,
    );
  }
}
